import { NATIVE_BUNDLE_ID } from './appOrigin';

/**
 * The custom-scheme return trip from a Stripe payment.
 *
 * ## Why a custom scheme and not a Universal Link
 *
 * Stripe sends the browser to `{requester}/payment/success?session_id=…` — an https URL on
 * the brand's own domain — and the obvious answer is to claim that URL natively so the OS
 * opens the app instead. It does not work. **A universal link only opens an app when a human
 * taps it**: a 3xx redirect does not qualify, and Stripe's return is a redirect with no tap
 * anywhere in it. Inside the in-app browser the app itself presented it is worse still — the
 * app never left the foreground, so iOS has nothing to "open" and the link simply resolves in
 * the sheet. This is the same wall every OAuth-in-SFSafariViewController integration hits.
 *
 * A custom scheme has no such restriction and is already registered for Auth0
 * (`CFBundleURLTypes` on iOS, an intent filter on Android), so a redirect to it from the
 * browser re-enters the app reliably. See docs/capacitor-mobile-app-feasibility.md §5 item 6.
 *
 * ## The shape of the trip
 *
 * 1. The API marks `success_url` as app-originated, because only it knows — the app sends
 *    `X-LMS-Client` and the URL is built server-side.
 *    See docs/api-app-checkout-return-requirement.md.
 * 2. `/payment/success` **on the website** sees the marker and redirects here.
 * 3. The app's `appUrlOpen` listener routes to `/payment/success` **in the app**, where the
 *    bearer token already exists and the existing verification runs unchanged.
 *
 * Both ends live in this module so the URL the website emits and the URL the app parses
 * cannot drift apart.
 */

/** Host segment of the deep link. Mirrored in AndroidManifest.xml, which matches on host. */
const PAYMENT_RETURN_HOST = 'payment';

/** The `client` values the API writes onto a return URL. See functions/nativeClient.ts. */
const NATIVE_CLIENTS = ['capacitor-ios', 'capacitor-android'];

/**
 * The deep link the website sends a paying app user back through.
 *
 * Built from `NATIVE_BUNDLE_ID` rather than a literal so it cannot drift from the scheme
 * actually registered with the OS — changing the bundle ID already means re-registering
 * Auth0 and the association files, and this moves with it.
 */
export function nativePaymentReturnUrl(sessionId: string): string {
   return `${NATIVE_BUNDLE_ID}://${PAYMENT_RETURN_HOST}/success?session_id=${encodeURIComponent(sessionId)}`;
}

/**
 * The deep link the website sends an app user back through when they **abandon** a checkout.
 *
 * Stripe's `cancel_url` is `{requester}{cancelUrl}`, and `cancelUrl` is whatever page the
 * purchase started from — `/lms/membership/{slug}`, `/lms/courses/{slug}`,
 * `/lms/premium/{slug}` and so on. So unlike success there is no single landing route to give
 * bespoke handling to; the marker arrives on arbitrary product pages, and any new purchasable
 * page type would silently lose the bounce. `middleware/002-native-return.global.ts` catches
 * them all instead, and hands the destination here.
 *
 * Reuses the `payment` host so neither platform needs a native change: Android's manifest
 * already declares that host, and iOS claims the whole scheme without naming one.
 */
export function nativeReturnUrl(path: string): string {
   return `${NATIVE_BUNDLE_ID}://${PAYMENT_RETURN_HOST}/return?path=${encodeURIComponent(path)}`;
}

/**
 * The in-app route for a payment-return deep link, or `null` for any other URL.
 *
 * Returning `null` rather than throwing is what lets the `appUrlOpen` listener use this as a
 * classifier: anything that is not a payment return keeps falling through to the auth
 * branches below it.
 *
 * The `client` marker is deliberately **not** carried into the app route. It is what tells
 * the *website* to bounce, so echoing it back would send the app's own success page
 * bouncing again.
 */
export function resolvePaymentReturnRoute(url: string): string | null {
   const parsed = parseDeepLink(url);

   if (!parsed) return null;
   if (parsed.protocol !== `${NATIVE_BUNDLE_ID}:`) return null;
   if (parsed.hostname !== PAYMENT_RETURN_HOST) return null;

   // The generic return, used by the cancel path — carries wherever the user should land.
   if (parsed.pathname === '/return') return safeInAppPath(parsed.searchParams.get('path'));

   const sessionId = parsed.searchParams.get('session_id');

   // No session id is still a payment return and still belongs on that page — it already
   // handles the missing-session case by sending the user to their subscriptions.
   if (!sessionId) return '/payment/success';

   return `/payment/success?session_id=${encodeURIComponent(sessionId)}`;
}

/**
 * Whether a `/payment/success` visit is a packaged-app purchase that should be handed back
 * to the app.
 *
 * Matches the two values the API actually sends and nothing else. The API's own guidance is
 * to key on those literals rather than on "a marker is present": the parameter is a client
 * identifier, not a native flag, so a future value could name something that is not a
 * Capacitor shell and must not be bounced to a custom scheme. The earlier `app` spelling
 * this file accepted was never shipped — the header route was taken instead, which is why
 * the value is platform-specific.
 *
 * Normalised before comparison because the API tolerates casing and surrounding whitespace
 * on the way in, so those can survive into a URL.
 *
 * Unmarked visits are ordinary web traffic and must render the page as they always have.
 *
 * This is a routing hint and nothing more. It arrives in a query string the user can edit,
 * so it must never gate access: entitlement comes from the Stripe webhook, and the page
 * still verifies the session against the API.
 */
export function isAppOriginatedPaymentReturn(client: unknown): boolean {
   if (typeof client !== 'string') return false;

   return NATIVE_CLIENTS.includes(client.trim().toLowerCase());
}

/**
 * A deep link arrives from outside the app and its `path` is attacker-supplyable, so only an
 * unambiguous in-app route is accepted. A single leading slash and nothing else: `//host` is
 * protocol-relative and `\\` is treated as a slash by some parsers, either of which would
 * point the router somewhere it should not go.
 */
function safeInAppPath(path: string | null): string | null {
   if (!path) return null;
   if (!path.startsWith('/')) return null;
   if (path.startsWith('//')) return null;
   if (path.includes('\\')) return null;

   return path;
}

/** `URL` throws on anything unparseable, and a deep link arrives from outside the app. */
function parseDeepLink(url: string): URL | null {
   try {
      return new URL(url);
   } catch {
      return null;
   }
}
